<template>
  <FlyModal class="feedback-dialog" @close="onClose">
    <template #header>
      <h3 class="fly-text fly-text--h3">
        Help Us Deliver An Even Better Experience!
      </h3>

      <FlyButton
        class="fly-button close-button fly-button--icon"
        type="button"
        @click="onClose"
      >
        <FlyIcon name="Close" />
      </FlyButton>
    </template>
    <template #default>
      <div class="dialog confirm alert text-center">
        <div class="dialog-text">
          <template v-if="selectedOption === 'Feedback'">
            <div class="sub-title">Send feedback</div>
            <div class="sub-title-description">
              Help us improve Integrate.io! Share feedback, ideas, and how you'd
              use new features!
              <small>
                You can also speak with live technical support
                <a class="link" @click="showIntercom">here</a>
              </small>
            </div>
          </template>

          <template v-if="selectedOption === 'Feature Request'">
            <div class="sub-title">We want to hear your feature requests</div>
            <div class="sub-title-description">
              Help us improve Integrate.io! Share feedback, ideas, and how you'd
              use new features!
              <small>
                You can also speak with live technical support
                <a class="link" @click="showIntercom">here</a>
              </small>
            </div>
          </template>

          <template v-if="selectedOption === 'Escalate'">
            <div class="sub-title">Escalate to Integrate.io leadership</div>
            <div class="sub-title-description">
              Got something mission-critical or urgent? This goes directly to
              our C-level team.
              <small>
                You can also speak with live technical support
                <a class="link" @click="showIntercom">here</a>
              </small>
            </div>
          </template>

          <div class="fly-box fly-tab-group">
            <div
              v-for="(tab, index) in tabs"
              :key="index"
              class="fly-text fly-text--tab fly-tab-group-item"
              :class="{ active: selectedOption === tab }"
              @click="selectedOption = tab"
            >
              {{ tab }}
            </div>
          </div>

          <textarea
            v-if="selectedOption === 'Feedback'"
            v-model="feedbackContent"
            class="form-control"
            placeholder="Write your feedback here..."
          ></textarea>

          <div v-if="selectedOption === 'Feature Request'" class="description">
            Click the button below to see our public-facing customer feature
            request board. You can upvote the features you'd like to see us
            build next or add new requests for features you don't see listed
            yet. Each quarter we dedicated product development cycles to
            requests from these requests.
          </div>

          <textarea
            v-if="selectedOption === 'Escalate'"
            v-model="escalateContent"
            class="form-control"
            placeholder="This is urgent..."
          ></textarea>
        </div>
      </div>
    </template>
    <template #footer>
      <FlyButton
        class="fly-button fly-button--secondary cancel-button"
        type="button"
        @click="onClose"
      >
        Cancel
      </FlyButton>
      <FlyButton
        v-if="selectedOption === 'Feedback' || selectedOption === 'Escalate'"
        class="fly-button fly-button--primary confirm-button"
        type="submit"
        :disabled="
          (selectedOption === 'Feedback' && !feedbackContent) ||
          (selectedOption === 'Escalate' && !escalateContent)
        "
        @click="onSend"
      >
        Send
      </FlyButton>
      <FlyButton
        v-if="selectedOption === 'Feature Request'"
        class="fly-button fly-button--primary confirm-button"
        @click="requestFeature"
      >
        Request Feature
      </FlyButton>
    </template>
  </FlyModal>
</template>

<script>
import FlyModal from 'shared/components/Modal/FlyModal.vue';
import FlyButton from 'shared/components/Button/FlyButton.vue';
import FlyIcon from 'shared/components/Icon/FlyIcon.vue';
import { FEEDBACK_MODAL } from 'web/modal/modalKeys';
import { setModalOpenState } from 'shared/store/modal/modalMutationTypes';
import { mapMutations, mapActions } from 'vuex';
import { showToastAction } from 'shared/stores/toast/toastActionTypes';
import TOAST_TYPE_ENUM from 'shared/components/Toast/TOAST_TYPE_ENUM';
import { USER_API_URL } from 'web/user/userEnum.js';

export default {
  name: 'FeedbackDialog',
  components: {
    FlyModal,
    FlyButton,
    FlyIcon,
  },
  data() {
    return {
      selectedOption: 'Feedback',
      feedbackContent: '',
      escalateContent: '',
      tabs: ['Feedback', 'Feature Request', 'Escalate'],
    };
  },
  computed: {},
  methods: {
    ...mapActions({
      showToastAction,
    }),
    ...mapMutations({
      setModalOpenState,
    }),
    onClose() {
      this.setModalOpenState({
        key: FEEDBACK_MODAL,
        open: false,
      });
    },

    async onSend() {
      const content =
        this.selectedOption === 'Feedback'
          ? this.feedbackContent
          : this.escalateContent;
      const endpoint =
        this.selectedOption === 'Feedback'
          ? 'feedback-request/'
          : 'escalate-request/';

      try {
        await fetch(`${USER_API_URL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            content,
          }),
        });

        this.showToastAction({
          key: 'FeedbackSuccessToast',
          title:
            'Thank you for your feedback! We will get back to you as soon as possible.',
          type: TOAST_TYPE_ENUM.SUCCESS,
          duration: 10000,
        });
        this.onClose();
      } catch (error) {
        console.error('Error sending feedback', error);
        this.showToastAction({
          key: 'FeedbackErrorToast',
          title: 'Failed to send feedback. Please try again.',
          type: TOAST_TYPE_ENUM.ERROR,
        });
      }
    },

    requestFeature() {
      window.open('https://integrateio.canny.io/feature-requests', '_blank');
    },

    showIntercom() {
      if (window.Intercom) {
        window.Intercom('show');
      }
    },
  },
};
</script>

<style lang="scss">
.feedback-dialog {
  .fly-tab-group {
    padding: 0;

    .fly-tab-group-item {
      cursor: pointer;
      padding: 12px 16px;
      text-decoration: none;

      &.active {
        color: #177af0;
        font-weight: 500;
        box-shadow: inset 0 -2px 0 0 #177af0;
      }
    }
  }

  .fly-modal--content {
    width: 100%;
  }

  min-height: 400px;

  .dialog-text {
    align-items: flex-start;
  }

  hr {
    width: 100%;

    @media (max-height: 680px) {
      display: none;
    }
  }

  .link {
    cursor: pointer;
    color: #177af0;
  }

  .dialog {
    width: 100%;
    padding: 20px;
  }

  .sub-title {
    font-size: 18px;
    font-weight: 400;
    line-height: 130%;
    text-align: left;
    color: #2d3e4a;
    margin-top: 10px;
    font-weight: 600;
    margin-bottom: 10px;
    width: 100%;
  }

  .sub-title-description {
    font-size: 16px;
    font-weight: 400;
    line-height: 130%;
    text-align: left;
    color: #2d3e4a;
    width: 100%;
    margin-bottom: 10px;

    small {
      font-size: 14px;
      font-weight: 400;
      line-height: 130%;
      text-align: left;
      color: #2d3e4a;
      display: block;
      margin-top: 5px;
      margin-bottom: 10px;
    }
  }

  textarea {
    height: 200px;
    width: 100%;
    margin: 20px 0;
    padding: 10px;
    border-radius: 8px;

    @media (max-height: 800px) {
      height: 100px;
    }

    @media (max-height: 680px) {
      height: 80px;
    }
  }

  .description {
    text-align: left;
    max-width: 800px;
    margin: 20px 0;
  }
}
</style>
